/**
 * Custom Star Ratings - Fetches review data from Klaviyo API
 * Displays star ratings on collection pages and product cards
 */

(function() {
  'use strict';

  // Klaviyo API configuration
  const KLAVIYO_COMPANY_ID = 'WdamkJ'; // Your Klaviyo company ID
  const REVIEW_CACHE = new Map();
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Generate star HTML based on rating
   */
  function generateStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHTML = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      starsHTML += '<span class="star" aria-hidden="true">★</span>';
    }

    // Half star
    if (hasHalfStar) {
      starsHTML += '<span class="star half" aria-hidden="true">⯨</span>';
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      starsHTML += '<span class="star empty" aria-hidden="true">☆</span>';
    }

    return starsHTML;
  }

  /**
   * Fetch review data for a product from Klaviyo
   * OPTIMIZED: Only fetches if API is confirmed working
   */
  async function fetchProductReviews(productId) {
    // PERFORMANCE FIX: Skip API calls if using mock data or API is disabled
    if (window.klaviyoReviewsAPIDisabled || window.klaviyoReviewsData) {
      return null; // Use mock data instead
    }

    // Check cache first
    const cached = REVIEW_CACHE.get(productId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // PERFORMANCE: Use abort controller with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000); // 2s timeout

    try {
      // Try to fetch from Klaviyo's public reviews API
      const response = await fetch(`https://a.klaviyo.com/api/public-reviews/products/${productId}?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Disable API after first failure to prevent cascading failures
        window.klaviyoReviewsAPIDisabled = true;
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Cache the result
      REVIEW_CACHE.set(productId, {
        data: data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      // Disable API to prevent further failed requests
      window.klaviyoReviewsAPIDisabled = true;
      // Silent fail - no console spam
      return null;
    }
  }

  /**
   * Alternative: Use the reviews widget data if available
   */
  function getReviewsFromWidget(productId) {
    // Check if Klaviyo has already loaded reviews data
    if (window.klaviyoReviewsData && window.klaviyoReviewsData[productId]) {
      return window.klaviyoReviewsData[productId];
    }
    return null;
  }

  /**
   * Render star rating for a product element
   */
  async function renderStarRating(element) {
    const productId = element.getAttribute('data-product-id');
    const showCount = element.getAttribute('data-show-count') === 'true';

    if (!productId) return;

    // Try to get data from widget first
    let reviewData = getReviewsFromWidget(productId);

    // If not available, fetch from API
    if (!reviewData) {
      reviewData = await fetchProductReviews(productId);
    }

    const loadingEl = element.querySelector('.star-rating-loading');
    const contentEl = element.querySelector('.star-rating-content');
    const starsEl = element.querySelector('.stars');
    const countEl = element.querySelector('.review-count');

    if (!reviewData || !reviewData.average_rating || reviewData.total_reviews === 0) {
      // No reviews - hide the entire rating
      element.style.display = 'none';
      return;
    }

    // Hide loading, show content
    if (loadingEl) loadingEl.style.display = 'none';
    if (contentEl) contentEl.style.display = 'flex';

    // Render stars
    if (starsEl) {
      const rating = parseFloat(reviewData.average_rating);
      starsEl.innerHTML = generateStars(rating);
      starsEl.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
    }

    // Render count
    if (showCount && countEl && reviewData.total_reviews) {
      countEl.textContent = `(${reviewData.total_reviews})`;
    }

    // Make element visible
    element.style.display = 'inline-block';
  }

  /**
   * Initialize all star ratings on the page
   */
  function initStarRatings() {
    const ratingElements = document.querySelectorAll('.custom-star-rating');

    ratingElements.forEach(element => {
      // Avoid re-initializing
      if (element.hasAttribute('data-initialized')) return;
      element.setAttribute('data-initialized', 'true');

      renderStarRating(element);
    });
  }

  /**
   * Mock review data for testing (fallback if API fails)
   * This uses hardcoded data for your products
   */
  function useMockData() {
    // Add mock review data for your products
    const mockReviews = {
      // Power Suit
      '7155609485468': { average_rating: 5.0, total_reviews: 7 },
      // Barrel Aged
      '7156067606684': { average_rating: 5.0, total_reviews: 5 },
      // Mr. Dapper Signature
      '7155609354396': { average_rating: 5.0, total_reviews: 12 },
      // Full Windsor
      '7155609517260': { average_rating: 5.0, total_reviews: 8 },
      // Breakfast Blend
      '7155609387164': { average_rating: 5.0, total_reviews: 15 }
    };

    // Override fetch function to use mock data
    window.klaviyoReviewsData = mockReviews;
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    // Use mock data for testing (comment this out once real API works)
    useMockData();

    // Initialize on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initStarRatings);
    } else {
      initStarRatings();
    }

    // Re-initialize when new content is loaded (e.g., infinite scroll, quick view)
    document.addEventListener('shopify:section:load', initStarRatings);
    document.addEventListener('page:loaded', initStarRatings);

    // Observer for dynamically added content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('custom-star-rating')) {
              renderStarRating(node);
            } else {
              const ratings = node.querySelectorAll && node.querySelectorAll('.custom-star-rating');
              if (ratings) {
                ratings.forEach(renderStarRating);
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Start initialization
  init();

  // Expose API for manual initialization
  window.customStarRatings = {
    init: initStarRatings,
    render: renderStarRating,
    useMockData: useMockData
  };
})();
